import { getSafeDistanceMiles, normalizeCoordinates } from "./locationUtils";

export const COMMUNITY_ALERT_TYPE = "local_alert";
export const COMMUNITY_ALERT_RADIUS_MILES = 100;
export const COMMUNITY_ALERT_DURATION_OPTIONS = [
  { value: "6h", label: "6 hours", hours: 6 },
  { value: "12h", label: "12 hours", hours: 12 },
  { value: "24h", label: "24 hours", hours: 24 },
  { value: "3d", label: "3 days", hours: 72 },
  { value: "7d", label: "7 days", hours: 168 },
  { value: "until_resolved", label: "Until I resolve it", hours: null },
];

export const getTimestampDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isCommunityAlertPost = (post = {}) =>
  (post.type || post.category) === COMMUNITY_ALERT_TYPE;

export const isEligibleCommunityPost = (post = {}) =>
  post.isArchived !== true &&
  post.archived !== true &&
  post.isHidden !== true &&
  post.hidden !== true &&
  post.deleted !== true &&
  (post.status || "active") === "active";

export const hasAlertLifecycleMetadata = (post = {}) =>
  post.alertStatus === "active" || post.alertStatus === "resolved";

export const getAlertExpirationDate = (post = {}) =>
  getTimestampDate(post.alertExpiresAt);

export const isCommunityAlertExpired = (post = {}, now = new Date()) => {
  const expiresAt = getAlertExpirationDate(post);
  if (!expiresAt) return false;
  return expiresAt.getTime() <= now.getTime();
};

export const isActiveCommunityAlert = (post = {}, options = {}) => {
  const now = options.now || new Date();

  return (
    isCommunityAlertPost(post) &&
    isEligibleCommunityPost(post) &&
    hasAlertLifecycleMetadata(post) &&
    post.alertStatus !== "resolved" &&
    !isCommunityAlertExpired(post, now)
  );
};

export const isResolvedCommunityAlert = (post = {}) =>
  isCommunityAlertPost(post) &&
  hasAlertLifecycleMetadata(post) &&
  post.alertStatus === "resolved";

export const getAlertExpiresAtForDuration = (durationValue, fromDate = new Date()) => {
  const option = COMMUNITY_ALERT_DURATION_OPTIONS.find(
    (item) => item.value === durationValue
  );
  if (!option || option.hours == null) return null;

  return new Date(fromDate.getTime() + option.hours * 60 * 60 * 1000);
};

export const getCommunityAlertDistance = (alert = {}, viewerLocation = null) => {
  const viewerCoordinates = normalizeCoordinates(viewerLocation);
  if (!viewerCoordinates) return null;

  return getSafeDistanceMiles(viewerCoordinates, alert);
};

export const hasUsableCommunityAlertViewerLocation = (viewerLocation = null) =>
  Boolean(normalizeCoordinates(viewerLocation));

export const isRelevantCommunityAlert = (alert = {}, viewerLocation = null, options = {}) => {
  const radiusMiles = options.radiusMiles ?? COMMUNITY_ALERT_RADIUS_MILES;
  const viewerCoordinates = normalizeCoordinates(viewerLocation);
  if (!viewerCoordinates) return true;

  const distance = getCommunityAlertDistance(alert, viewerCoordinates);
  if (typeof distance !== "number") return false;
  return distance <= radiusMiles;
};

export const getActiveRelevantCommunityAlerts = (
  alerts = [],
  viewerLocation = null,
  options = {}
) => {
  const now = options.now || new Date();
  return alerts
    .filter((alert) => isActiveCommunityAlert(alert, { now }))
    .filter((alert) => isRelevantCommunityAlert(alert, viewerLocation, options))
    .map((alert) => ({
      ...alert,
      distanceMiles: getCommunityAlertDistance(alert, viewerLocation),
    }))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

export const getCommunityAlertCountLabel = (count = 0, options = {}) =>
  `${count} Community Alert${count === 1 ? "" : "s"}${
    options.nearby === true ? " nearby" : ""
  }`;

export const getCommunityAlertStatusLabel = (post = {}, now = new Date()) => {
  if (isResolvedCommunityAlert(post)) return "Resolved";
  if (isCommunityAlertExpired(post, now)) return "Expired";
  if (isActiveCommunityAlert(post, { now })) return "Community Alert";
  if (isCommunityAlertPost(post)) return "Community Alert";
  return "";
};

export const getCommunityAlertExpiryLabel = (post = {}, now = new Date()) => {
  const expiresAt = getAlertExpirationDate(post);
  if (!expiresAt) return "Until resolved";

  const diffMs = expiresAt.getTime() - now.getTime();
  if (diffMs <= 0) return "Expired";

  const diffHours = Math.ceil(diffMs / (60 * 60 * 1000));
  if (diffHours <= 24) return `Expires in ${diffHours} hour${diffHours === 1 ? "" : "s"}`;

  const diffDays = Math.ceil(diffHours / 24);
  return `Expires in ${diffDays} day${diffDays === 1 ? "" : "s"}`;
};
